import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Form, Grid, Icon, Loader } from "semantic-ui-react";
import { addNewVendor, getBorrowerVendorList, getLoanDrawAttachments, getLoanDrawDetails, getLoanDrawDetailVendors, updateLoanDrawApplicationDetails } from "../../../api/BorrowerApi";
import { addMockNewVendor, getMockBorrowerVendorList, getMockLoanDetailVendors, getMockLoanDrawAttachments, getMockLoanDrawDetails, updateMockLoanDrawApplicationDetails } from "../../../api/mock/MockBorrowerApi";
import { combineAddresses, convertToDateString, convertToDollar, formatDataProperties, getDropdownOptions, guid, handleAddressValue, handleErrorResponse, handleFormChange, validateEmails, validateObj } from "../../../Helpers";
import { IBorrowerVenderObj, IDrawAttachmentObj, IDrawAttachmentView, ILoanDrawDetailObj, ILoanDrawDetailView, INewVendor, IUpdatedLoanDetail } from "../../interfaces/Borrower";
import "../../../styles/borrower/home.scss";
import BCButton from "../../ui/BCButton";
import BCDashboard from "../../ui/BCDashboard";
import BCEmptyState from "../../ui/BCEmptyState";
import BCFormInput from "../../ui/BCFormInput";
import BCMessage from "../../ui/BCMessage";
import BCTable from "../../ui/BCTable";
import BCModal from "../../ui/BCModal";
import BCValidationMessage from "../../ui/BCValidationMessage";
import { GlobalConstants } from "../../constants/GlobalConstants";
import BCUploadBox from "../../ui/BCUploadBox";
import BCMenuBar from "../../ui/BCMenuBar";
import BWExportButton from "../BWExportButton";
import BWSearchBox from "../BWSearchBox";
import { FeatureFlags } from "../../constants/LocalServerConstants";

const BWLoanDrawDetails = ({
  isGreaterThanMobile, 
  isGreaterThanTablet, 
  isSidebar, 
  isMessage, 
  setIsMessage, 
  errorResponse, 
  setErrorResponse, 
  setSuccessMessage,
  proxyUser, 
  loanProps
}) => {
  const [isLoading, setIsLoading] = useState(false as boolean);
  const [isFirstLoad, setIsFirstLoad] = useState(true as boolean);
  const [loanDrawDetailsData, setLoanDrawDetailsData] = useState([] as ILoanDrawDetailObj[]);
  const [borrowerVenderList, setBorrowerVenderList] = useState([] as IBorrowerVenderObj[]);
  const [updatedLoanDetailObj, setUpdatedLoanDetailObj] = useState({} as any);
  const [isNewVendorModalOpen, setIsNewVendorModalOpen] = useState(false as boolean);
  const [newVendor, setNewVendor] = useState({} as INewVendor | any);
  const [invalidProps, setInvalidProps] = useState([] as string[]);
  const [isAttachmentsModalOpen, setIsAttachmentsModalOpen] = useState(false as boolean);
  const [isAttachmentsMessage, setIsAttachmentsMessage] = useState(false as boolean);
  const [drawAttachmentList, setDrawAttachmentList] = useState([] as IDrawAttachmentObj[]);

  const history = useHistory();
  const handleError = useCallback(() => {
    history.push("/error");
  }, [history]);
  const handleBackHome = useCallback(() => {
    history.push("/");
  }, [history]);
  const handleToProcessLoanDrawDetails = useCallback((id, loanDrawRequestInfo) => {
    history.push(`/loandrawdetail/process/${id}`, {"loanDrawRequestInfo": {...loanDrawRequestInfo}, ...loanProps});
  }, [history]);

  const updateDisplayDetails = (details) => {
    setLoanDrawDetailsData(details);
    convertDetailsToTableFormat(details);
  };

  const editRequestedAmount = (detail, details) => {
    const handleSwitchToEditMode = () => {
      detail.editRequestedAmount = true;
      details.map(x => {
        if (x.id !== detail.id) {
          x.editRequestedAmount = false;
        }

        return x;
      });

      updateDisplayDetails([...details]);
    };

    const handleRequestedAmountChange = (value) => {
      detail.drawRequestAmount = value;
      updateDisplayDetails([...details]);

      const selectedDetail = updatedLoanDetailObj.loanDrawApplicationDetails.find(x => x.id === detail.id);
      if (selectedDetail) {
        selectedDetail.drawRequestAmount = value;
      } else {
        const newDetail = {
          "id" : detail.id,
          "drawRequestAmount": value,
          "detailDirectPay": detail.detailDirectPay,
        };

        updatedLoanDetailObj.loanDrawApplicationDetails.push({...newDetail});
      }

      setUpdatedLoanDetailObj(updatedLoanDetailObj);
      setLoanDrawDetailsData([...details]);
    };

    return (
      <div>
        {!detail.editRequestedAmount || detail.editRequestedAmount === false ? 
          <div><Icon name="edit" onClick={() => handleSwitchToEditMode()} />
            <span>{convertToDollar(parseInt(detail.drawRequestAmount))}</span> 
          </div>
          : <BCFormInput 
            value={detail.drawRequestAmount}
            type="input"
            obj={updatedLoanDetailObj}
            field="drawRequestAmount"
            handleFormChange={(value) =>  handleRequestedAmountChange(value)}
            required
          />
        }
      </div>
    );
  };

  const convertDetailsToTableFormat = (details) => {
    const loanDrawDetailsTable = [] as ILoanDrawDetailView[];
    details.map(detail => {
      loanDrawDetailsTable.push({
        "loanDrawApplicationId": detail.loanDrawApplicationId,
        "id": detail.id,
        "directPay": handleDirectPayEdit(detail, details, borrowerVenderList),
        "category": detail.category,
        "subCategory": detail.subCategory,
        "budget": convertToDollar(detail.budgetAmount),
        "previouslyDrawn": convertToDollar(detail.disbursedAmount),
        "budgetRemaining": convertToDollar(detail.availableBudgetAmount),
        "available": convertToDollar(detail.availableWithoutInspection),
        "requestedAmount": editRequestedAmount(detail, details),
        "remaining": convertToDollar(detail.availableBudgetAmount -  detail.drawRequestAmount),
        "lastPercentWorkComplete": detail.lastPercentWorkComplete,
        "sortOrder": detail.sortOrder,
      } as ILoanDrawDetailView);
    });

    const formattedDrawDetails = formatDataProperties(loanDrawDetailsTable, [
      {field: "available", action: "rename", name: "Available(Previously inspected)"}, 
      {field: "lastPercentWorkComplete", action: "rename", name: "Last % Complete"},
      {field: "loanDetailVendors", action: "rename", name: "loanDetailVendors"},
    ]);

    return formattedDrawDetails;
  };

  const convertAttachmentsToTableFormat = (attachments) => {
    const attachmentTable = [] as IDrawAttachmentView[];
    attachments.map(attachment => {
      attachmentTable.push({
        "id": attachment.id,
        "attachment": attachment.attachment,
        "attachedBy": attachment.attachedBy,
        "attachedOn": convertToDateString(attachment.attachedOn, "datetimewithoutsecond"),
      } as IDrawAttachmentView);
    });

    return formatDataProperties(attachmentTable);
  };

  const handleDirectPayEdit = (detail, details, borrowerVenderList) => {
    const editDirectPay = detail.directPay || detail.detailDirectPay;
    const handleDirectPayChange = () => {
      detail.detailDirectPay = detail.detailDirectPay ? false : true;
      updateDisplayDetails([...details]);
    };

    const handleShowDirectPay = () => {
      detail.showDetailVendors = detail.showDetailVendors ? !detail.showDetailVendors : true;
      updateDisplayDetails([...details]);
    };

    const handleAddPay = (loanDetailId) => {
      detail.showDetailVendors = true;
      const newPay = {
        loanDrawApplication_DetailId: loanDetailId,
        id: guid(),
        vendorName: null,
        referenceNo: null,
        drawRequestAmount: null,
        address: null,
        city: null,
        state: null,
        zip: null,
        paidToVendor: true,
        email: null,
        requiresApprovalForPayment: null,
        allowedToUpdate: true,
        isNewRow: true
      };

      if (detail.loanDetailVendors) {
        detail.loanDetailVendors.push({...newPay});
      } else {
        detail.loanDetailVendors = [{...newPay}];
      }

      updateDisplayDetails([...details]);
    };

    const handleCloseAddPay = () => {
      handleShowDirectPay();
      const lastDetail = detail.loanDetailVendors[detail.loanDetailVendors.length - 1];
      if (!lastDetail.vendorName && !lastDetail.referenceNo && !lastDetail.requiresApprovalForPayment && lastDetail.drawRequestAmount === null) {
        detail.loanDetailVendors.pop();
      }

      updateDisplayDetails([...details]);
    };

    const handleDeletePay = (payId) => {
      detail.loanDetailVendors = detail.loanDetailVendors.filter(pay => pay.id !== payId);
      updateDisplayDetails([...details]);
    };

    const handleChangeDetailVendor = (value, field, padId, formType : string | undefined = undefined) => {
      const selectedPay = detail.loanDetailVendors.find(pay => pay.id === padId);
      if (selectedPay) {
        if (formType === GlobalConstants.FormInputTypes.CHECKBOX) {
          selectedPay[field] = selectedPay[field] ? true : false;
        } else {
          selectedPay[field] = value;
        }
      }

      updateDisplayDetails([...details]);
    };

    return (
      <React.Fragment>
        <div className="borrower-table-input-plus-button">
          <BCFormInput 
            inputKey={`direct-pay-${detail.id}`}
            labelClass="borrower-table-checkbox"
            value={editDirectPay}
            type={GlobalConstants.FormInputTypes.CHECKBOX}
            handleFormChange={() => handleDirectPayChange()}
            disabled={detail.directPay && detail.detailDirectPay}
          />
          {(editDirectPay && !detail.hasVendor) && <Button
            className="table-button" 
            icon
            color={detail.showDetailVendors ? "red" : "green"}
            inverted
            onClick={() => {detail.showDetailVendors ? handleCloseAddPay() : handleAddPay(detail.id);}}
          >
            <div className="gmr-button-container">
              <Icon name={detail.showDetailVendors ? "close" : "add"} />
              {!isGreaterThanMobile ? null : <div className="table-button-text">
                {detail.showDetailVendors ? "Close" : "Add Pay"}
              </div>}
            </div>
          </Button>}
          {(editDirectPay && detail.hasVendor) && <Button
            className="table-button" 
            icon
            color="green"
            inverted
            onClick={() => handleShowDirectPay()}
          >
            <div className="gmr-button-container">
              <Icon name={`caret square ${detail.showDetailVendors ? "down": "right"}`} /> 
              {!isGreaterThanMobile ? null : <div className="table-button-text">
                {detail.showDetailVendors ? "Hide List": "Show List"}
              </div>}
            </div>
          </Button>}
          {(editDirectPay && detail.showDetailVendors) && <Button
            className="table-button" 
            icon
            color="green"
            inverted
            onClick={() => setIsNewVendorModalOpen(true)}
          >
            <div className="gmr-button-container">
              <Icon name="add" />
                New Vendor
            </div>
          </Button>}
          {detail.showDetailVendors && <React.Fragment>
            <table className="borrower-directpay-table">
              <tr>
                <td className="table-function-column"><Icon name="add" onClick={() => handleAddPay(detail.id) } title="Add Direct Pay" /></td>
                <td>Vendor Name</td>
                <td>I Paid Vendor</td>
                <td>Reference No</td>
                <td>Requires Approval</td>
                <td>Requested Amount</td>
              </tr>
            {detail.loanDetailVendors.map(pay => {
              return (
                <tr key={`loan-detail-${pay.id}`}>
                  <td className="table-function-column"><Icon name="trash alternate" onClick={() => handleDeletePay(pay.id)} title="Delete Direct Pay" /></td>
                  <td><div className="borrower-directpay-input-dropdown">
                    <BCFormInput
                      inputKey={detail.id}
                      type="dropdown"
                      dropdownOptions={getDropdownOptions(borrowerVenderList, ["name"], "name")}
                      value={pay.vendorName}
                      field="marketValue"
                      fluid={false}
                      obj={pay}
                      inputType={GlobalConstants.InputTypes.INT}
                      handleFormChange={(value, field) => handleChangeDetailVendor(value, field, pay.id)}
                    />
                  </div></td>
                  <td><div className="borrower-directpay-input-row">
                    <BCFormInput
                      type={GlobalConstants.FormInputTypes.CHECKBOX}
                      value={pay.paidToVendor}
                      field="paidToVendor"
                      handleFormChange={(value, field) => handleChangeDetailVendor(value, field, pay.id, GlobalConstants.FormInputTypes.CHECKBOX)}
                    />
                  </div></td>
                  <td><div className="borrower-directpay-input-row">
                    <BCFormInput
                      type={GlobalConstants.FormInputTypes.INPUT}
                      value={pay.referenceNo}
                      field="referenceNo"
                      inputType={GlobalConstants.InputTypes.STRING}
                      handleFormChange={(value, field) => handleChangeDetailVendor(value, field, pay.id)}
                    />
                  </div></td>
                  <td><div className="borrower-directpay-input-row">
                    <BCFormInput
                      type={GlobalConstants.FormInputTypes.CHECKBOX}
                      value={pay.requiresApprovalForPayment}
                      field="requiresApprovalForPayment"
                      handleFormChange={(value, field) => handleChangeDetailVendor(value, field, pay.id, GlobalConstants.FormInputTypes.CHECKBOX)}
                    />
                  </div></td>
                  <td><div className="borrower-directpay-input-row">
                    <BCFormInput
                      type="input"
                      value={pay.drawRequestAmount}
                      field="drawRequestAmount"
                      inputType={GlobalConstants.InputTypes.DOLLAR}
                      handleFormChange={(value, field) => handleChangeDetailVendor(value, field, pay.id)}
                    />
                  </div></td>
                </tr>
              );
            })}
            </table>
          </React.Fragment>}
        </div>
      </React.Fragment>
    );
  };

  const handleNewVendorModalSubmit = async () => {
    setInvalidProps([]);
    let notValidProperties = [] as string[];
    notValidProperties = validateObj(newVendor, ["firstName", "lastName", "name", "vendorEmail", "physicalStreet", "physicalZipCode", "physicalCity", "physicalCounty", "physicalState"]);

    if (!newVendor.isMailingSameAsPhysical) {
      notValidProperties = notValidProperties.concat(validateObj(newVendor, ["mailingStreet", "mailingZipCode"]));
    }

    if (newVendor.vendorEmail) {
      const notValidEmails = validateEmails(newVendor, ["vendorEmail"]);
      if (notValidEmails && notValidEmails.length > 0) {
        notValidProperties.push("custom-vendor-invalid-email");
      }
    }
    // TODO: ask about email validation for existing email
    if (notValidProperties.length === 0) {
      if (newVendor.physicalAddressLine2) {
        newVendor.physicalStreet = combineAddresses(newVendor.physicalStreet, newVendor.physicalAddressLine2);
      }

      if (newVendor.mailingAddressLine2) {
        newVendor.mailingStreet = combineAddresses(newVendor.mailingStreet, newVendor.physicalAddressLine2);
      }

      const addVendorSuccess = await addNewVendorAsync(newVendor);
      if (addVendorSuccess) {
        setIsMessage(false);
        setSuccessMessage("Your profile has been created.");
        setTimeout(() => { 
          setSuccessMessage("");
        }, 5000);
        setIsNewVendorModalOpen(false);
        setNewVendor({});
      }
    }
    else {
      setTimeout(() => { 
        setInvalidProps(notValidProperties);
      }, 50);
    }
  };

  const addNewVendorAsync = async (vendorObj) => {
    try {
      const response = FeatureFlags.IS_MOCK ? await addMockNewVendor(vendorObj) : await addNewVendor(vendorObj, proxyUser && proxyUser.id) as any;
      if (response && response.ok) {
        return true;
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
        setIsLoading(false);
      }
    }
    catch (error) {
      setIsMessage(false);
    }
  };

  const updateLoanDrawApplicationDetailsAsync = async (updatedLoanDetailObj) => {
    try {
      const response = FeatureFlags.IS_MOCK ? await updateMockLoanDrawApplicationDetails() : await updateLoanDrawApplicationDetails(updatedLoanDetailObj, proxyUser && proxyUser.id);
      if (response && response.ok) {
        const loanDrawRequestDetailInfo = await response.json();
        setIsMessage(false);
        handleToProcessLoanDrawDetails(loanProps.loanId, {...loanDrawRequestDetailInfo});
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
      }
    }
    catch (error) {
      setIsMessage(false);
    }
  };

  const getLoanDetailVendorsAsync = async (loanDetailId) => {
    try {
      const response = FeatureFlags.IS_MOCK ? await getMockLoanDetailVendors() : await getLoanDrawDetailVendors(loanDetailId, proxyUser && proxyUser.id);
      if (response && response.ok) {
        setIsMessage(false);
        const loanDetailVendors = await response.json();
        return loanDetailVendors;
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
      }
    }
    catch (error) {
      setIsMessage(false);
    }

    setIsLoading(false);
    return false;
  };

  const LoanDetailsLayout = [
    [
      {
        title: <React.Fragment>
        <div className="breadcrumbs">
          <div className="breadcrumbs-home" onClick={() => handleBackHome()}><Icon name="home" />Home</div>
          <div> &gt; </div>
          <div className="breadcrumbs-current">{loanProps.loan} - {loanProps.loanProperty}</div>
        </div>
      </React.Fragment>,
        size: 16,
        headerOptions: [
          <BCButton handleOnClick={() => console.log("export")}>Export</BCButton>,
          <BCButton handleOnClick={() => console.log("save")}>Save</BCButton>,
          <BCButton handleOnClick={() => setIsAttachmentsModalOpen(true)}>Attachments</BCButton>,
          <BCButton handleOnClick={() => updateLoanDrawApplicationDetailsAsync(updatedLoanDetailObj)}>Submit</BCButton>,
          <BCButton handleOnClick={() => handleBackHome()}>Close</BCButton>,
        ],
        content: <React.Fragment>
          <BCMessage
            animation={"fly down"}
            duration={750}
            visible={isMessage}
            transitionOnMount
            styles="full-width"
            onDismiss={() => setIsMessage(false)}
            errorResponse={errorResponse}
          />
          {loanDrawDetailsData.length > 0 ? <React.Fragment>
            <div>
              <BCTable 
                data={convertDetailsToTableFormat(loanDrawDetailsData)}
                dataRules={[
                  { field: "Loan Draw Application Id", rule: "hide" },
                  { field: "Id", rule: "hide" },
                  { field: "Sort Order", rule: "hide" },
                ]}
                selectable
                editMode={true}
                overflowY
                overflowX
                dataSingleLine
                sortable={["Category", "Sub Category", "Budget", "Previously Drawn", "Budget Remaining", "Available(Previously inspected)", "Requested Amount", "Remaining", "Last % Complete"]}
                pagination={17}
              /></div>
            </React.Fragment>
            : <BCEmptyState
              emptyStateClass="home-content-empty-state"
              icon="file alternate"
              text="There is no loan details."
            />
          }
        </React.Fragment>
      }
    ]
  ];

  const NewVendorModalContent = <Form className="primary-form">

    <Grid>
      <Grid.Row className="application-row">
        <Grid.Column width={isGreaterThanMobile ? 8 : 16}>
          <BCFormInput
            labelClass="gmr-background-label"
            type={GlobalConstants.FormInputTypes.INPUT}
            inputKey="vendor"
            label="First Name"
            placeholder="John"
            obj={newVendor}
            field="firstName"
            inputType={GlobalConstants.InputTypes.STRING}
            handleFormChange={(value, field) => handleFormChange(value, field, newVendor, setNewVendor)}
            required
          />
          <BCValidationMessage invalidProps={invalidProps} prop="firstName" />
        </Grid.Column>
        <Grid.Column width={isGreaterThanMobile ? 8 : 16}>
          <BCFormInput
            labelClass="gmr-background-label"
            type={GlobalConstants.FormInputTypes.INPUT}
            inputKey="vendor"
            label="Last Name"
            placeholder="Smith"
            obj={newVendor}
            field="lastName"
            inputType={GlobalConstants.InputTypes.STRING}
            handleFormChange={(value, field) => handleFormChange(value, field, newVendor, setNewVendor)}
            required
          />
          <BCValidationMessage invalidProps={invalidProps} prop="lastName" />
        </Grid.Column>  
      </Grid.Row>
      <Grid.Row className="application-row">
        <Grid.Column width={isGreaterThanMobile ? 8 : 16}>
          <BCFormInput
            labelClass="gmr-background-label"
            type={GlobalConstants.FormInputTypes.INPUT}
            inputKey="vendor"
            label="Organization Name"
            obj={newVendor}
            value={newVendor.name}
            inputType={GlobalConstants.InputTypes.STRING}
            field="name"
            handleFormChange={(value, field) => handleFormChange(value, field, newVendor, setNewVendor)}
            required
          />
          <BCValidationMessage invalidProps={invalidProps} prop="name" />
        </Grid.Column>
        <Grid.Column width={isGreaterThanMobile ? 8 : 16}>
          <BCFormInput
            labelClass="gmr-background-label"
            type="dropdown"
            label="Organization Type"
            placeholder="Please Select..."
            obj={newVendor}
            value={newVendor.OrganizationTypeId}
            field="entitySubTypeId"
            // dropdownOptions={getDropdownOptions(gmrMetadata.entitySubTypes, ["id"], "name")} // TODO: find out dropdown list
            handleFormChange={(value, field) => handleFormChange(value, field, newVendor, setNewVendor)}
            required
          /> 
        </Grid.Column>
      </Grid.Row>
      <Grid.Row className="application-row">
        <Grid.Column width={16}>
        <BCFormInput
            labelClass="gmr-background-label"
            type={GlobalConstants.FormInputTypes.INPUT}
            inputKey="vendor"
            label="Email"
            placeholder="john.smith@gmail.com"
            obj={newVendor}
            field="vendorEmail"
            inputType={GlobalConstants.InputTypes.STRING}
            handleFormChange={(value, field) => handleFormChange(value, field, newVendor, setNewVendor, undefined, undefined, 100)}
            required
          />
          <BCValidationMessage invalidProps={invalidProps} prop="vendorEmail" />
          <BCValidationMessage 
            invalidProps={invalidProps} 
            prop="custom-new-vendor-invalid-email"
            propText="This email is invalid."
          />
          <BCValidationMessage
            invalidProps={invalidProps} 
            prop="custom-vendor-email"
            propText="This email has been used."
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row className="application-row">
        <Grid.Column width={16}>
          <BCFormInput
            label="Campany Address"
            inputKey="vendor"
            labelClass="gmr-background-label"
            type={GlobalConstants.FormInputTypes.PLACES}
            placeDetailsObject={newVendor}
            setPlaceDetailsObject={setNewVendor}
            placeholder="1234 Main Street, Seattle, WA 98052, USA"
            defaultValue={handleAddressValue({
              address: newVendor.physicalStreet,
              city: newVendor.physicalCity,
              state: newVendor.physicalState,
              zip: newVendor.physicalZipCode
            })}
            placeFieldsNameOverrides={[
              {
                addressComponent: "address",
                field: "physicalStreet"
              },
              {
                addressComponent: "city",
                field: "physicalCity"
              },
              {
                addressComponent: "state",
                field: "physicalState"
              },
              {
                addressComponent: "county",
                field: "physicalCounty"
              },
              {
                addressComponent: "zip",
                field: "physicalZipCode"
              },
            ]}
            required
          />
          <BCValidationMessage invalidProps={invalidProps} prop="physicalZipCode" propText="Zip code is required." />
          <BCValidationMessage invalidProps={invalidProps} prop="physicalStreet" propText="Street name is required." />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={16}>
          <BCFormInput
            labelClass="gmr-background-label"
            type={GlobalConstants.FormInputTypes.INPUT}
            inputKey="vendor"
            label="Address Line 2"
            placeholder="Apartment, suite, unit, building, floor, etc."
            obj={newVendor}
            field="physicalAddressLine2"
            inputType={GlobalConstants.InputTypes.STRING}
            handleFormChange={(value, field) => handleFormChange(value, field, newVendor, setNewVendor)}
          />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row className="application-row">
        <Grid.Column width={16}>
          <BCFormInput 
            labelClass="gmr-background-label"
            type={GlobalConstants.FormInputTypes.CHECKBOX}
            checkboxText="Company Address is same as Mailing Address"
            obj={newVendor}
            field="isMailingSameAsPhysical"
            value={newVendor.isMailingSameAsPhysical}
            handleFormChange={(value, field) => handleFormChange(value, field, newVendor, setNewVendor)}
          />
        </Grid.Column>
      </Grid.Row>
      {!newVendor.isMailingSameAsPhysical && <React.Fragment>
        <Grid.Row className="application-row">
          <Grid.Column width={16}>
            <BCFormInput
              label="Mailing Address"
              inputKey="vendor"
              labelClass="gmr-background-label"
              type={GlobalConstants.FormInputTypes.PLACES}
              placeDetailsObject={newVendor}
              setPlaceDetailsObject={setNewVendor}
              placeholder="1234 Main Street, Seattle, WA 98052, USA"
              defaultValue={handleAddressValue({
                address: newVendor.mailingStreet,
                city: newVendor.mailingCityName,
                state: newVendor.mailingStateName,
                zip: newVendor.mailingZipCode
              })}
              placeFieldsNameOverrides={[
                {
                  addressComponent: "address",
                  field: "mailingStreet"
                },
                {
                  addressComponent: "city",
                  field: "mailingCityName"
                },
                {
                  addressComponent: "state",
                  field: "mailingStateName"
                },
                {
                  addressComponent: "county",
                  field: "mailingCountyName"
                },
                {
                  addressComponent: "zip",
                  field: "mailingZipCode"
                },
              ]}
            />
            <BCValidationMessage invalidProps={invalidProps} prop="mailingZipCode" propText="Zip code is required." />
            <BCValidationMessage invalidProps={invalidProps} prop="mailingStreet" propText="Street name is required." />
          </Grid.Column>
          <Grid.Column width={16}>
            <BCFormInput
              labelClass="gmr-background-label"
              type={GlobalConstants.FormInputTypes.INPUT}
              inputKey="vendor"
              label="Address Line 2"
              placeholder="Apartment, suite, unit, building, floor, etc."
              obj={newVendor}
              field="mailingAddressLine2"
              inputType={GlobalConstants.InputTypes.STRING}
              handleFormChange={(value, field) => handleFormChange(value, field, newVendor, setNewVendor)}
            />
          </Grid.Column>
        </Grid.Row>
      </React.Fragment>}
    </Grid>
  </Form>;

  useEffect(() => {
    const getLoanDrawDetailsAysnc = async (loanDrawApplicationId) => {
      setIsLoading(true);
      try {
        const response = FeatureFlags.IS_MOCK ? await getMockLoanDrawDetails() : await getLoanDrawDetails(loanDrawApplicationId, proxyUser && proxyUser.id);
        if (response && response.ok) {
          setIsMessage(false);
          const loanDrawDetails = await response.json() as ILoanDrawDetailObj[];
          if (loanDrawDetails.length > 0) {
            loanDrawDetails.forEach(async (detail) => {
              if (detail.hasVendor === true) {
                setIsLoading(true);
                const detailVendors = await getLoanDetailVendorsAsync(detail.id);
                detail.loanDetailVendors = detailVendors;
              }
            });
          }
  
          setLoanDrawDetailsData(loanDrawDetails.sort((a, b) => a.sortOrder - b.sortOrder));
  
          const editLoanDetail : IUpdatedLoanDetail = {
            id: loanDrawApplicationId,
            loanDrawApplicationDetails: [],
            loanDrawApplicationDetailVendors: [],
            deletedVendors: [],
            isSubmit: false,
            directPay: loanDrawDetails[0].directPay
          };
  
          setUpdatedLoanDetailObj({...editLoanDetail});
        }
        else {
          await handleErrorResponse(response, setIsMessage, setErrorResponse);
        }
      }
      catch (error) {
        handleError();
      }
  
      setIsLoading(false);
    };

    const getBorrowerVendorListAsync = async () => {
      try {
        const response = FeatureFlags.IS_MOCK ? await getMockBorrowerVendorList() : await getBorrowerVendorList(loanProps.loanId, proxyUser && proxyUser.id);
        if (response && response.ok) {
          setIsMessage(false);
          const borrowerVendors = await response.json();
          setBorrowerVenderList(borrowerVendors);
        }
        else {
          await handleErrorResponse(response, setIsMessage, setErrorResponse);
        }
      }
      catch (error) {
        setIsMessage(false);
      }
    };

    const getLoanDrawAttachmentsAsync = async (loanDrawApplicationId) => {
      try {
        const response = FeatureFlags.IS_MOCK ? await getMockLoanDrawAttachments() : await getLoanDrawAttachments(loanDrawApplicationId, proxyUser && proxyUser.id);
        if (response && response.ok) {
          setIsAttachmentsMessage(false);
          const attachments = await response.json();
          setDrawAttachmentList(attachments);
        }
        else {
          await handleErrorResponse(response, setIsAttachmentsMessage, setErrorResponse);
        }
      }
      catch (error) {
        setIsAttachmentsMessage(false);
      }
    };

    if (isFirstLoad) {
      getLoanDrawDetailsAysnc(loanProps.loanId);
      getLoanDrawAttachmentsAsync(loanProps.loanId);
      getBorrowerVendorListAsync();
    }
  }, [setIsMessage, setErrorResponse, handleError, proxyUser, isFirstLoad, borrowerVenderList]);

  useEffect(() => {
    setIsFirstLoad(false);
  }, []);

  return (
    <div className={(isGreaterThanTablet && isSidebar) ? "landing-page hide-sidebar" : "landing-page sidebar"}>
      <BCModal
        isModalOpen={isNewVendorModalOpen}
        closeOnlyByButton={true}
        onClose={() => setIsNewVendorModalOpen(false)}
        onOpen={() => setIsNewVendorModalOpen(true)}
        header="Add New Vendor"
        content={NewVendorModalContent}
        actions={<Grid>
          <Grid.Row columns={3}>
            <Grid.Column>
              <Button fluid className="bp-button" onClick={() => {handleNewVendorModalSubmit(); setIsNewVendorModalOpen(true);}}>
                <div className="gmr-button-container">
                  <div className="gmr-button-text">Save and New</div>
                </div>
              </Button>
            </Grid.Column>
            <Grid.Column>
              <Button fluid className="bp-button" onClick={() => setIsNewVendorModalOpen(false)}>
                <div className="gmr-button-container">
                  <div className="gmr-button-text">Save and Close</div>
                </div>
              </Button>
            </Grid.Column>
            <Grid.Column>
              <Button fluid onClick={() => {setIsNewVendorModalOpen(false); setNewVendor({});}}>
                <div className="gmr-button-container">
                  <div className="gmr-button-text">Close</div>
                </div>
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>}
      />
      <BCModal
        isModalOpen={isAttachmentsModalOpen}
        closeOnlyByButton={true}
        onClose={() => setIsAttachmentsModalOpen(false)}
        onOpen={() => setIsAttachmentsModalOpen(true)}
        header="Manage Attachments"
        content={<Form className="primary-form" autoComplete="off">
          <BCMessage
            animation={"fly down"}
            duration={750}
            visible={isAttachmentsMessage}
            transitionOnMount
            styles="full-width"
            onDismiss={() => setIsMessage(false)}
            errorResponse={errorResponse}
          />
          <BCMenuBar 
            isGreaterThanMobile={isGreaterThanMobile}
            leftChildren={[<BWExportButton />]}
            rightChildren={[<BWSearchBox />]}
          />
          {drawAttachmentList && drawAttachmentList.length > 0 ?
            <BCTable 
              dataRules={[{ field: "Id", rule: "hide" }]}
              data={convertAttachmentsToTableFormat(drawAttachmentList)}
              selectable
              overflowX
              dataSingleLine
              sortable={["attachment", "attachedBy", "attachedOn"]}
              pagination={8}
              ellipsis
              editMode={true}
              ellipsisHandlers={[{action: (i, item) => console.log(i, item), text: "Delete"}]}
            /> :
            <BCEmptyState
              emptyStateClass="inspection-empty-state"
              icon="file"
              text="Looks like there's no document uploaded for this loan draw."
            />
          }
          <div className="loandraw-upload"><BCUploadBox
            verticalClass="upload-container vertical-button modal-upload"
            uploadTitle="Drag your document here or browse"
            uploadText="JPEGs, PDFs, Excel and Word Documents accepted"
            // handleUploadFiles={handleStageUpload} TODO: handler for uploading document
          /></div>
        </Form>}
        actions={<Grid>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Button fluid onClick={() => setIsAttachmentsModalOpen(false)}>
                <div className="gmr-button-container">
                  <div className="gmr-button-text">Close</div>
                </div>
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>}
      />
      {isLoading ? <Loader active inline="centered" size="big" /> : <React.Fragment>
        <BCDashboard
          DashboardLayout={LoanDetailsLayout}
          dashboardHeaderOptions={true}
        />
      </React.Fragment>}
      {FeatureFlags.IS_DEBUG && <React.Fragment>
        <button onClick={() => console.log(loanDrawDetailsData)}>loanDrawDetailsData</button>
        <button onClick={() => console.log(borrowerVenderList)}>borrowerVenderList</button>
        <button onClick={() => console.log(drawAttachmentList)}>drawAttachmentList</button>
        <button onClick={() => console.log(updatedLoanDetailObj)}>updatedLoanDetailObj</button>
      </React.Fragment>}
    </div>
  );
};

export default BWLoanDrawDetails;